import { useState } from 'react';
import { apiCall } from '../api/client';
import CollapsibleEditorCard from './CollapsibleEditorCard';
import { Field, Section, inputCls } from './LessonEditorFields';

// Generated practice has its own authoring contract. It does not fetch or edit
// lessonContent, objectives, examples, or newly introduced grammar concepts.
export default function GrammarDrillEditor({ level, courseId, token, controls, onSaved, columns = [], collapsed, onToggle }) {
  const [title, setTitle] = useState(level.title || '');
  const [sourceColumn, setSourceColumn] = useState(level.sourceColumn ?? 0);
  const [targetColumn, setTargetColumn] = useState(level.targetColumn ?? 1);
  const [grammarPoint, setGrammarPoint] = useState(level.grammarPoint || '');
  const [drillCount, setDrillCount] = useState(level.drillCount || 10);
  const [generationInstructions, setGenerationInstructions] = useState(level.generationInstructions || '');
  const [dialectNotes, setDialectNotes] = useState((level.dialectNotes || []).join('\n'));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  function edit(setter, value) {
    setSaved(false);
    setter(value);
  }

  async function save() {
    setError('');
    if (!title.trim() || !grammarPoint.trim()) { setError('Add a title and the grammar point to practise.'); return; }
    if (!Number.isInteger(Number(drillCount)) || Number(drillCount) < 5 || Number(drillCount) > 20) {
      setError('Choose between 5 and 20 phrases per round.'); return;
    }
    if (sourceColumn === targetColumn || !columns[sourceColumn] || !columns[targetColumn]) {
      setError('Choose different prompt and answer languages.'); return;
    }
    setSaving(true);
    try {
      const updated = await apiCall('PATCH', `/courses/${courseId}/levels/${level.id}`, {
        title: title.trim(), sourceColumn, targetColumn,
        grammarPoint: grammarPoint.trim(), drillCount: Number(drillCount),
        generationInstructions: generationInstructions.trim(),
        dialectNotes: dialectNotes.split('\n').map(line => line.trim()).filter(Boolean),
      }, token);
      onSaved?.(updated);
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Could not save the drill.');
    } finally {
      setSaving(false);
    }
  }

  return <CollapsibleEditorCard title={title || level.title} subtitle="Grammar drill"
    controls={controls} collapsed={collapsed} onToggle={onToggle}>
    <p className="text-sm text-amber-700">Practise a grammar point taught earlier in the course. The AI generates translation phrases, then uses missed answers to focus the next round.</p>
    <fieldset disabled={saving} className="space-y-4">
      <Section step={1} title="Drill name and translation direction">
        <Field label="Drill title" hint="Shown on the course page and drill screen. This display name is not sent to the AI; use Grammar point below to set its focus.">
          <input value={title} maxLength={200} onChange={e => edit(setTitle, e.target.value)} className={inputCls} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Prompt language" hint="Tells the AI which language to write the phrases in. Clarification replies also use this language.">
            <select value={sourceColumn} onChange={e => edit(setSourceColumn, Number(e.target.value))} className={`${inputCls} bg-white`}>
              {columns.map((column, i) => <option key={i} value={i} disabled={i === targetColumn}>{column}</option>)}
            </select>
          </Field>
          <Field label="Answer language" hint="Tells the AI which language to provide accepted translations in. The learner types their answers in this language.">
            <select value={targetColumn} onChange={e => edit(setTargetColumn, Number(e.target.value))} className={`${inputCls} bg-white`}>
              {columns.map((column, i) => <option key={i} value={i} disabled={i === sourceColumn}>{column}</option>)}
            </select>
          </Field>
        </div>
        <button type="button" onClick={() => { edit(setSourceColumn, targetColumn); edit(setTargetColumn, sourceColumn); }} className="text-sm text-amber-700 underline">Swap languages</button>
      </Section>

      <Section step={2} title="What the AI should practise"
        blurb="These settings control each generated round. Place the drill after the lesson that teaches its grammar point.">
        <Field label="Grammar point" hint="The specific focus sent to the phrase generator and clarification tutor, and shown to the learner. Name a concept already taught, including the verbs, tense, or forms to practise.">
          <input value={grammarPoint} maxLength={200} onChange={e => edit(setGrammarPoint, e.target.value)} placeholder="e.g. Present tense of être and aller: all subject pronouns" className={inputCls} />
        </Field>
        <Field label="Phrases per round" hint="Requests this many distinct prompt/translation pairs from the AI for each round. Between 5 and 20; the default is 10.">
          <input type="number" min="5" max="20" value={drillCount} onChange={e => edit(setDrillCount, e.target.value)} className={inputCls} />
        </Field>
        <Field label="Phrase generation instructions (optional)" hint="Sent only to the phrase generator to control sentence patterns and difficulty. Learners see the resulting phrases, not this text. Leave blank for varied practice of the grammar point.">
          <textarea value={generationInstructions} maxLength={2000} onChange={e => edit(setGenerationInstructions, e.target.value)} rows={4}
            placeholder="Use short affirmative sentences. Mix singular and plural subjects evenly. Avoid questions and negation." className={`${inputCls} resize-y`} />
        </Field>
        <Field label="Dialect guidance (optional)" hint="One note per line. Sent to both the phrase generator and clarification tutor, alongside the course's dialect setting. Use this for regional wording or formality preferences.">
          <textarea value={dialectNotes} onChange={e => edit(setDialectNotes, e.target.value)} rows={2} placeholder="Use informal spoken French." className={`${inputCls} resize-y`} />
        </Field>
      </Section>
    </fieldset>

    <section className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-700 space-y-2">
      <h3 className="font-semibold text-amber-800">Context added automatically</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Grammar concepts taught in earlier lessons, following the course order.</li>
        <li>Vocabulary from earlier course levels, plus words this learner has already studied.</li>
        <li>For a weak-spot round: missed phrases, accepted translations, and the learner's submitted answers. The AI uses these to target mistakes with new phrases.</li>
        <li>For clarification: the current phrase, its translations, and the learner's question.</li>
      </ul>
      <p>This drill adds no new taught concepts or vocabulary. Re-publish the course after changing earlier teaching content to refresh the AI's context.</p>
    </section>
    {error && <p role="alert" className="text-red-500 text-sm">{error}</p>}
    <button onClick={save} disabled={saving || !title.trim() || !grammarPoint.trim()} className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm font-medium px-5 py-2 rounded-lg transition">
      {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save Drill'}
    </button>
  </CollapsibleEditorCard>;
}
